// ============================================================
// CART CONTEXT — benessere-posturale
// ============================================================

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  ReactNode,
} from 'react';
import {
  CartContextType,
  CartCourse,
  CartItem,
  CartState,
} from '../types/cart';
import { courseService } from '../services';

// ─── Storage key ─────────────────────────────────────────────
const STORAGE_KEY_CART = 'bp_cart';

// ─── Stato iniziale ──────────────────────────────────────────
const initialState: CartState = {
  items: [],
  isOpen: false,
};

// ─── Reducer ─────────────────────────────────────────────────
type CartAction =
  | { type: 'ADD_ITEM'; payload: CartCourse }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'CLEAR_CART' }
  | { type: 'OPEN_CART' }
  | { type: 'CLOSE_CART' }
  | { type: 'TOGGLE_CART' }
  | { type: 'HYDRATE'; payload: CartItem[] };

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'HYDRATE':
      return { ...state, items: action.payload };

    case 'ADD_ITEM': {
      // Evita duplicati
      const exists = state.items.some((i) => i.course.id === action.payload.id);
      if (exists) return { ...state, isOpen: true };
      const newItem: CartItem = {
        course: action.payload,
        addedAt: new Date().toISOString(),
      };
      return { ...state, items: [...state.items, newItem], isOpen: true };
    }

    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter((i) => i.course.id !== action.payload),
      };

    case 'CLEAR_CART':
      return { ...state, items: [] };

    case 'OPEN_CART':
      return { ...state, isOpen: true };

    case 'CLOSE_CART':
      return { ...state, isOpen: false };

    case 'TOGGLE_CART':
      return { ...state, isOpen: !state.isOpen };

    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────
const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// ─── Provider ────────────────────────────────────────────────
interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  // Ripristina carrello da localStorage al mount, riallineando ogni prezzo
  // al catalogo reale: un articolo aggiunto prima di una modifica prezzo
  // (o rimasto in cache da tempo) non deve mostrare un importo obsoleto.
  useEffect(() => {
    let items: CartItem[] = [];
    try {
      const stored = localStorage.getItem(STORAGE_KEY_CART);
      items = stored ? JSON.parse(stored) : [];
    } catch {
      localStorage.removeItem(STORAGE_KEY_CART);
    }

    if (items.length === 0) {
      dispatch({ type: 'HYDRATE', payload: [] });
      return;
    }

    courseService
      .getCatalog()
      .then((catalog) => {
        const reconciled = items
          .map((item) => {
            const live = catalog.find((c) => c.id === item.course.id);
            // Corso non più nel catalogo o diventato non acquistabile
            // (price null) → rimosso dal carrello.
            if (!live || live.price === null) return null;
            return { ...item, course: { ...item.course, price: live.price } };
          })
          .filter((i): i is CartItem => i !== null);
        dispatch({ type: 'HYDRATE', payload: reconciled });
      })
      .catch(() => {
        // Catalogo irraggiungibile: mostra comunque il carrello salvato,
        // il totale verrà comunque ricalcolato dal backend al checkout.
        dispatch({ type: 'HYDRATE', payload: items });
      });
  }, []);

  // Persiste items in localStorage ad ogni modifica
  // Nota: isOpen non viene persistito — il drawer riparte sempre chiuso
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_CART, JSON.stringify(state.items));
  }, [state.items]);

  // ── Azioni ───────────────────────────────────────────────
  const addItem = useCallback((course: CartCourse) => {
    dispatch({ type: 'ADD_ITEM', payload: course });
  }, []);

  const removeItem = useCallback((courseId: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: courseId });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
  }, []);

  const openCart = useCallback(() => {
    dispatch({ type: 'OPEN_CART' });
  }, []);

  const closeCart = useCallback(() => {
    dispatch({ type: 'CLOSE_CART' });
  }, []);

  const toggleCart = useCallback(() => {
    dispatch({ type: 'TOGGLE_CART' });
  }, []);

  const isInCart = useCallback(
    (courseId: string) => state.items.some((i) => i.course.id === courseId),
    [state.items]
  );

  // ── Totali calcolati (memoizzati) ────────────────────────
  const totalItems = useMemo(() => state.items.length, [state.items]);

  const totalPrice = useMemo(
    () => state.items.reduce((sum, i) => sum + i.course.price, 0),
    [state.items]
  );

  const value: CartContextType = {
    ...state,
    addItem,
    removeItem,
    clearCart,
    openCart,
    closeCart,
    toggleCart,
    isInCart,
    totalItems,
    totalPrice,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
